import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import {
  SupportChannelLabel,
  SupportPriorityBadge,
  SupportStatusBadge,
} from "../../components/support/SupportBadges";
import { Spinner } from "../../components/Spinner";
import { fetchSupportStats, isApiError } from "../../lib/support-api";
import type { SupportStats } from "../../lib/support-types";
import { useConfig } from "../../state/config-context";

function Breakdown({
  title,
  entries,
  render,
}: {
  title: string;
  entries: [string, number][];
  render: (key: string) => ReactNode;
}): ReactNode {
  return (
    <article className="card card--wide">
      <h2 className="card__title">{title}</h2>
      {entries.length === 0 ? (
        <p className="muted">—</p>
      ) : (
        <ul className="tag-list">
          {entries.map(([key, count]) => (
            <li className="tag" key={key}>
              {render(key)}
              <span className="muted"> · {count}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function SupportDashboardInner(): ReactNode {
  const { t } = useConfig();
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setStats(await fetchSupportStats());
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const cards: { key: string; label: string; value: string }[] =
    stats === null
      ? []
      : [
          { key: "open", label: t("support.dashboard.openTotal"), value: String(stats.open_total) },
          {
            key: "unassigned",
            label: t("support.dashboard.unassigned"),
            value: String(stats.unassigned_open),
          },
          {
            key: "sla-breached",
            label: t("support.dashboard.slaBreached"),
            value: String(stats.sla_breached),
          },
          {
            key: "sla-active",
            label: t("support.dashboard.slaActive"),
            value: String(stats.sla_active),
          },
          {
            key: "replies",
            label: t("support.dashboard.repliesTotal"),
            value: String(stats.replies_total),
          },
        ];

  return (
    <section className="page" aria-labelledby="support-dashboard-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="support-dashboard-title">{t("support.dashboard.title")}</h1>
          <p className="muted">{t("support.dashboard.subtitle")}</p>
        </div>
        <Link className="btn btn--ghost" to="/support/tickets">
          {t("support.dashboard.viewTickets")}
        </Link>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && stats !== null ? (
        <>
          <div className="card-grid">
            {cards.map((card) => (
              <article className="card" key={card.key}>
                <h2 className="card__title">{card.label}</h2>
                <p className="card__value">{card.value}</p>
              </article>
            ))}
          </div>
          <Breakdown
            title={t("support.dashboard.byStatus")}
            entries={Object.entries(stats.tickets_by_status)}
            render={(key) => <SupportStatusBadge status={key} />}
          />
          <Breakdown
            title={t("support.dashboard.byPriority")}
            entries={Object.entries(stats.tickets_by_priority)}
            render={(key) => <SupportPriorityBadge priority={key} />}
          />
          <Breakdown
            title={t("support.dashboard.byChannel")}
            entries={Object.entries(stats.tickets_by_channel)}
            render={(key) => <SupportChannelLabel channel={key} />}
          />
        </>
      ) : null}
    </section>
  );
}

export function SupportDashboardPage(): ReactNode {
  return (
    <RequirePermission permission="support.ticket.read">
      <SupportDashboardInner />
    </RequirePermission>
  );
}